// src/ContactUs.jsx
import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FaInstagram,
  FaWhatsapp,
  FaChevronLeft,
  FaMoon,
  FaSun,
  FaSpinner,
  FaPaperPlane,
} from 'react-icons/fa';

const API_URL = process.env.REACT_APP_API_URL || 'https://api.mogym.ir';

const palette = {
  primary: '#2563eb',
  purple: '#7c3aed',
  ok: '#10b981',
  danger: '#ef4444',
};

export default function ContactUs() {
  const navigate = useNavigate();

  const [dark, setDark] = useState(true);
  const [name, setName] = useState('');
  const [msg, setMsg] = useState('');
  const [loading, setLoading] = useState(false);

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const colors = useMemo(() => {
    if (dark) {
      return {
        bg: '#070B14',
        bg2: '#08101C',
        bg3: '#0A1224',
        card: 'rgba(21,29,51,0.96)',
        card2: 'rgba(14,20,37,0.96)',
        text: '#FFFFFF',
        sub: '#B7C0D1',
        muted: '#8FA0BA',
        border: 'rgba(255,255,255,0.08)',
        inputBg: '#10182A',
        headerBg: 'rgba(10,15,30,0.80)',
        overlay: 'rgba(0,0,0,0.55)',
      };
    }

    return {
      bg: '#F8FAFF',
      bg2: '#F2F6FF',
      bg3: '#EDF2FF',
      card: 'rgba(255,255,255,0.96)',
      card2: 'rgba(245,248,255,0.96)',
      text: '#0F172A',
      sub: '#475569',
      muted: '#64748B',
      border: 'rgba(15,23,42,0.10)',
      inputBg: '#FFFFFF',
      headerBg: 'rgba(255,255,255,0.82)',
      overlay: 'rgba(15,23,42,0.28)',
    };
  }, [dark]);

  const submit = async () => {
    const token = localStorage.getItem('token');

    setError('');
    setSuccess('');

    if (!token) {
      localStorage.setItem(
        'redirectAfterLogin',
        JSON.stringify({
          to: '/contact-us',
        })
      );

      setError('برای ارسال پیام، لطفاً ابتدا وارد حساب شوید.');
      setTimeout(() => navigate('/login'), 700);
      return;
    }

    if (!name.trim() || !msg.trim()) {
      setError('لطفاً نام و پیام را کامل وارد کنید.');
      return;
    }

    try {
      setLoading(true);

      const res = await fetch(`${API_URL}/api/contact-us`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          name: name.trim(),
          message: msg.trim(),
        }),
      });

      if (!res.ok) {
        let serverMessage = '';
        try {
          const data = await res.json();
          serverMessage = data?.Message || data?.message || '';
        } catch {}

        throw new Error(serverMessage || 'send_error');
      }

      setSuccess('پیام شما با موفقیت ارسال شد.');
      setName('');
      setMsg('');
    } catch (err) {
      setError(err?.message && err.message !== 'send_error' ? err.message : 'ارسال پیام با خطا مواجه شد.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div dir="rtl" className={dark ? 'dark' : ''}>
      <div
        className="min-h-screen font-vazir relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${colors.bg}, ${colors.bg2}, ${colors.bg3})`,
          color: colors.text,
        }}
      >
        <div className="fixed top-20 right-[-80px] w-72 h-72 rounded-full bg-purple-500/20 blur-3xl pointer-events-none" />
        <div className="fixed bottom-20 left-[-80px] w-72 h-72 rounded-full bg-blue-500/20 blur-3xl pointer-events-none" />

        {loading && (
          <div
            className="fixed inset-0 z-[999] flex items-center justify-center"
            style={{ backgroundColor: colors.overlay }}
          >
            <div
              className="min-w-[130px] min-h-[130px] rounded-[24px] border flex flex-col items-center justify-center shadow-2xl"
              style={{
                background: `linear-gradient(135deg, ${colors.card}, ${colors.card2})`,
                borderColor: colors.border,
              }}
            >
              <FaSpinner className="animate-spin text-3xl" style={{ color: palette.primary }} />
              <div className="mt-4 text-sm font-bold">در حال ارسال...</div>
            </div>
          </div>
        )}

        <header className="relative z-20 max-w-6xl mx-auto px-4 pt-4">
          <div
            className="min-h-[64px] rounded-3xl border px-4 flex items-center justify-between backdrop-blur-xl shadow-lg"
            style={{
              backgroundColor: colors.headerBg,
              borderColor: colors.border,
            }}
          >
            <button
              type="button"
              onClick={() => navigate('/')}
              className="flex items-center gap-3"
            >
              <img src="/logo.png" alt="Mogym" className="w-11 h-11 rounded-2xl" />
              <div className="text-right">
                <div className="font-black text-lg">ارتباط با ما</div>
                <div className="hidden sm:block text-xs mt-1" style={{ color: colors.muted }}>
                  پاسخ‌گویی، پشتیبانی و پیشنهادها
                </div>
              </div>
            </button>

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="hidden sm:inline-flex px-4 py-2 rounded-2xl border text-sm font-bold"
                style={{
                  borderColor: colors.border,
                  backgroundColor: colors.inputBg,
                  color: colors.text,
                }}
              >
                صفحه اصلی
              </button>

              <button
                type="button"
                onClick={() => setDark((v) => !v)}
                className="w-11 h-11 rounded-2xl border flex items-center justify-center"
                style={{
                  background: dark
                    ? 'linear-gradient(135deg,#111827,#1F2937)'
                    : 'linear-gradient(135deg,#FFFFFF,#F1F5F9)',
                  borderColor: colors.border,
                  color: colors.text,
                }}
              >
                {dark ? <FaSun /> : <FaMoon />}
              </button>
            </div>
          </div>
        </header>

        <main className="relative z-10 max-w-6xl mx-auto px-4 py-10">
          <section className="text-center mb-8">
            <h1 className="text-3xl md:text-5xl font-black leading-[1.4]">
              خوشحال می‌شیم ازت بشنویم
            </h1>

            <p
              className="mt-4 max-w-2xl mx-auto text-sm md:text-base leading-8"
              style={{ color: colors.sub }}
            >
              اگر سوال، پیشنهاد یا مشکلی داری، از طریق فرم زیر یا شبکه‌های اجتماعی با ما در ارتباط باش.
            </p>
          </section>

          <div className="grid lg:grid-cols-[1.2fr_0.8fr] gap-5 items-start">
            <section
              className="rounded-[28px] border p-5 md:p-6 shadow-2xl backdrop-blur-xl"
              style={{
                background: `linear-gradient(135deg, ${colors.card}, ${colors.card2})`,
                borderColor: colors.border,
              }}
            >
              <div className="flex items-center justify-between gap-3 mb-5">
                <h2 className="text-xl font-black">فرم تماس</h2>
                <div
                  className="w-11 h-11 rounded-2xl flex items-center justify-center"
                  style={{ backgroundColor: 'rgba(37,99,235,0.12)', color: palette.primary }}
                >
                  <FaPaperPlane />
                </div>
              </div>

              <label className="block mb-2 text-sm font-bold">نام</label>
              <input
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  setError('');
                  setSuccess('');
                }}
                placeholder="نام شما"
                className="w-full rounded-2xl border px-4 py-4 outline-none text-right mb-4"
                style={{
                  backgroundColor: colors.inputBg,
                  borderColor: colors.border,
                  color: colors.text,
                }}
              />

              <label className="block mb-2 text-sm font-bold">پیام شما</label>
              <textarea
                value={msg}
                onChange={(e) => {
                  setMsg(e.target.value);
                  setError('');
                  setSuccess('');
                }}
                placeholder="پیام خودت را اینجا بنویس..."
                rows={7}
                className="w-full rounded-2xl border px-4 py-4 outline-none text-right resize-none mb-4 leading-7"
                style={{
                  backgroundColor: colors.inputBg,
                  borderColor: colors.border,
                  color: colors.text,
                }}
              />

              <button
                type="button"
                disabled={loading}
                onClick={submit}
                className="w-full h-14 rounded-2xl text-white font-black transition hover:scale-[1.01] disabled:opacity-70 disabled:hover:scale-100"
                style={{
                  background: `linear-gradient(135deg, ${palette.primary}, ${palette.purple})`,
                }}
              >
                {loading ? 'در حال ارسال...' : 'ارسال پیام'}
              </button>

              {error && (
                <div
                  className="mt-4 rounded-2xl border px-4 py-3 text-center text-sm font-bold"
                  style={{
                    borderColor: 'rgba(239,68,68,0.25)',
                    backgroundColor: dark ? 'rgba(127,29,29,0.20)' : 'rgba(254,242,242,0.96)',
                    color: palette.danger,
                  }}
                >
                  {error}
                </div>
              )}

              {success && (
                <div
                  className="mt-4 rounded-2xl border px-4 py-3 text-center text-sm font-bold"
                  style={{
                    borderColor: 'rgba(16,185,129,0.25)',
                    backgroundColor: dark ? 'rgba(6,78,59,0.22)' : 'rgba(236,253,245,0.96)',
                    color: palette.ok,
                  }}
                >
                  {success}
                </div>
              )}
            </section>

            <aside
              className="rounded-[28px] border p-5 md:p-6 shadow-2xl backdrop-blur-xl"
              style={{
                background: `linear-gradient(135deg, ${colors.card}, ${colors.card2})`,
                borderColor: colors.border,
              }}
            >
              <h2 className="text-xl font-black mb-5">راه‌های ارتباطی</h2>

              <ContactItem
                icon={<FaInstagram />}
                title="اینستاگرام"
                value="@mogym.ir"
                colors={colors}
                onClick={() => window.open('https://instagram.com/mogym.ir', '_blank', 'noopener,noreferrer')}
              />

              <ContactItem
                icon={<FaWhatsapp />}
                title="واتساپ"
                value="09031239853"
                colors={colors}
                onClick={() => window.open('https://wa.me/989031239853', '_blank', 'noopener,noreferrer')}
              />

              <div
                className="mt-5 rounded-3xl border p-4"
                style={{
                  borderColor: colors.border,
                  backgroundColor: dark ? 'rgba(255,255,255,0.035)' : 'rgba(15,23,42,0.035)',
                }}
              >
                <h3 className="font-black mb-2">پشتیبانی موجیم</h3>
                <p className="text-sm leading-7" style={{ color: colors.sub }}>
                  پیام‌های مربوط به برنامه تمرینی، رژیم غذایی، پرداخت و اصلاح برنامه را از همین بخش برای ما بفرست.
                </p>
              </div>
            </aside>
          </div>
        </main>
      </div>
    </div>
  );
}

function ContactItem({ icon, title, value, onClick, colors }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="w-full rounded-3xl border px-4 py-4 mb-3 flex items-center justify-between transition hover:scale-[1.01]"
      style={{
        borderColor: colors.border,
        backgroundColor: colors.inputBg,
        color: colors.text,
      }}
    >
      <div className="flex items-center gap-3">
        <div
          className="w-11 h-11 rounded-2xl flex items-center justify-center text-xl"
          style={{
            backgroundColor: 'rgba(37,99,235,0.10)',
            color: palette.primary,
          }}
        >
          {icon}
        </div>

        <div className="text-right">
          <div className="font-black text-sm">{title}</div>
          <div className="text-sm mt-1" style={{ color: colors.sub }}>
            {value}
          </div>
        </div>
      </div>

      <FaChevronLeft style={{ color: colors.sub }} />
    </button>
  );
}